import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Icon from '../comps/Icon';
import managetables from '../services/managetables';
import manageareas from '../services/manageareas';
import reservationsSvc from '../services/reservations';
import businessConfigSvc from '../services/businessConfig';
import openingHoursSvc from '../services/openingHours';
import * as engine from '../services/seatingEngine';
import '../style/css/liveboard2026.css';

/*
 * LiveBoard — the SIGNATURE operator screen (LIVE day-management board).
 *
 * Layout (RTL app): the FLOOR SKETCH is the dominant focus (~78–80% width, tall).
 * Everything else (compact stats + guest list + LIVE ops) lives in a COLLAPSIBLE
 * side PANEL on the physical LEFT ("צד שמאל"); in an RTL flex row the panel is
 * pinned left via `order` (see .lb-panel in the CSS). The panel can be minimized
 * (persisted in localStorage 'lbPanelCollapsed') so it never blocks the tables;
 * a floating expand button reopens it. On mobile (≤768px) the panel becomes a
 * bottom sheet toggled by a floating button. This is a LAYOUT restructure only —
 * all data/seating/drag/tap/add/edit logic is unchanged.
 *
 * DATA + LOGIC are now REAL-wired:
 *   - tables + zones come from managetables.getAllTables / manageareas.getAllArea (real)
 *   - reservations come from reservations.listToday (real; empty on a quiet day, no mock)
 *   - per-table free/occupied/upcoming state comes from the client seatingEngine
 *     (floorStateAt / isTableFree / suggestNextTable / occupancyAt) — no more
 *     self-contained floor logic here.
 *   - seating a guest calls reservations.assignTable and updates local state; if the
 *     act isn't live yet it falls back cleanly (offline demo action).
 */

/* ------------------------------------------------------------------ *
 *  Constants + helpers
 * ------------------------------------------------------------------ */

// Fallback dining duration (minutes) until the real businessConfig loads
// (businessConfig.maxTimeTableIsInUseInMinutes).
const SEAT_DUR_FALLBACK = 90;




// Time-scrubber slots — 17:00 → 23:30 in 30-min steps.
const TIME_SLOTS = (() => {
  const out = [];
  for (let m = 17 * 60; m <= 23 * 60 + 30; m += 30) {
    out.push(engine.toHHMM(m));
  }
  return out;
})();

/* ------------------------------------------------------------------ *
 *  Helpers
 * ------------------------------------------------------------------ */

const clockHHMM = (d) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// Nearest time-slot chip to "now" (so the scrubber can default sensibly).
const nearestSlot = (hhmm) => {
  const t = engine.toMinutes(hhmm);
  let best = TIME_SLOTS[0];
  let bestDiff = Infinity;
  TIME_SLOTS.forEach((s) => {
    const d = Math.abs(engine.toMinutes(s) - t);
    if (d < bestDiff) { bestDiff = d; best = s; }
  });
  return best;
};

const STATUS_LABEL = {
  waiting: 'ממתין',
  seated: 'ישוב',
  booked: 'הוזמן',
  arrived: 'הגיע',
  'no-show': 'לא הגיע',
  cancelled: 'בוטל',
};

const todayHe = () =>
  new Date().toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' });

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const LiveBoard = () => {
  // Real (or fallback) data.
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [zones, setZones] = useState([{ id: 0, name: 'כל האזורים' }]);
  const [live, setLive] = useState(false); // true once real reservationsList data arrives
  const [zoneId, setZoneId] = useState(0);

  // Board timing from business settings (הגדרות עסק). Fallbacks until getBusinessConfig loads:
  // seatingDuration = how long a party holds a table; upcomingLead = the X minutes before a
  // booked reservation that its table is flagged "upcoming/saved".
  const [seatingDuration, setSeatingDuration] = useState(SEAT_DUR_FALLBACK);
  const [upcomingLead, setUpcomingLead] = useState(engine.UPCOMING_LEAD);

  // Today's opening windows (type-1 weekly rows for today's weekday) → minutes ranges. Used to
  // decide, on releasing a table, whether the business is STILL OPEN (so we can offer to rebook
  // the just-freed table immediately). Empty until openingTimesList loads.
  const [todayWindows, setTodayWindows] = useState([]);

  // Load REAL tables + areas (zone tabs) + today's reservations on mount. No mock fallback —
  // a genuinely quiet day shows an empty board, never fabricated reservations.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [realTables, areaList] = await Promise.all([
          managetables.getAllTables(true),
          manageareas.getAllArea(true).catch(() => []),
        ]);
        if (!alive) return;
        setTables(Array.isArray(realTables) ? realTables : []);
        const areas = (Array.isArray(areaList) ? areaList : []).map((a) => ({ id: Number(a.id || a.ID), name: a.name }));
        setZones([{ id: 0, name: 'כל האזורים' }, ...areas]);
      } catch (e) { /* leave empty */ }

      try {
        const { rows, live: isLive } = await reservationsSvc.listToday([]);
        if (alive) {
          setReservations(Array.isArray(rows) ? rows : []);
          setLive(!!isLive);
        }
      } catch (e) { /* leave empty */ }

      // Real timing from business settings (falls back to the hardcoded defaults on error).
      try {
        const cfg = await businessConfigSvc.getConfig();
        if (alive && cfg) {
          if (cfg.maxTimeTableIsInUseInMinutes) setSeatingDuration(businessConfigSvc.effectiveHold(cfg));
          if (cfg.upcomingReservationLeadMinutes != null) setUpcomingLead(cfg.upcomingReservationLeadMinutes);
        }
      } catch (e) { /* keep fallbacks */ }

      // Opening windows NORMALIZED onto TODAY's 0–1440 timeline, so "open now?" is a plain
      // [start,end) test (no wrap logic). dayInWeek is 1=Sunday … 7=Saturday (date('w')+1).
      // An overnight row (end<=start) is split: today's evening part [start,1440), and — from
      // YESTERDAY's overnight row — this-morning's spillover [0,end). Times "HH:MM(:SS)"→minutes.
      try {
        const rows = await openingHoursSvc.listHours();
        if (alive && Array.isArray(rows)) {
          const today = new Date().getDay() + 1;                 // 1..7
          const yest = ((new Date().getDay() + 6) % 7) + 1;      // yesterday's dayInWeek
          const toMinSafe = (t) => engine.toMinutes(String(t).slice(0, 5));
          const wins = [];
          rows.filter((r) => Number(r.type) === 1).forEach((r) => {
            const s = toMinSafe(r.startTime);
            const e = toMinSafe(r.endTime);
            if (!Number.isFinite(s) || !Number.isFinite(e) || s === e) return;
            const dw = Number(r.dayInWeek);
            const overnight = e <= s;
            if (dw === today) wins.push(overnight ? { start: s, end: 1440 } : { start: s, end: e });
            if (dw === yest && overnight) wins.push({ start: 0, end: e }); // spilled into today's morning
          });
          setTodayWindows(wins);
        }
      } catch (e) { /* no hours → treated as closed for the rebook prompt */ }
    })();
    return () => { alive = false; };
  }, []);

  // Real-time: re-pull today's reservations periodically so new bookings (incl. public
  // bookings) and other-operator changes appear without a manual refresh. Only replaces the
  // list once real (live) data has been established, so a demo/offline board isn't wiped.
  useEffect(() => {
    let alive = true;
    const t = setInterval(async () => {
      try {
        const mySeq = reservationsSeqRef.current;
        const { rows, live: isLive } = await reservationsSvc.listToday([]);
        // Drop this poll if a local mutation happened while it was in flight (seq changed).
        if (alive && isLive && mySeq === reservationsSeqRef.current) {
          setReservations(Array.isArray(rows) ? rows : []);
        }
      } catch (e) { /* keep current */ }
    }, 45000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Refresh immediately when a reservation is booked elsewhere (global "הזמנת שולחן" modal).
  useEffect(() => {
    const h = async () => {
      try {
        const mySeq = reservationsSeqRef.current;
        const { rows, live: isLive } = await reservationsSvc.listToday([]);
        // Same seq guard as the poll — don't clobber a just-applied local mutation.
        if (isLive && mySeq === reservationsSeqRef.current) setReservations(Array.isArray(rows) ? rows : []);
      } catch (e) { /* keep current */ }
    };
    window.addEventListener('hoppa:reservations-changed', h);
    return () => window.removeEventListener('hoppa:reservations-changed', h);
  }, []);

  // Live wall clock.
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);

  // Time scrubber — the "viewing time". Defaults to the slot nearest to now.
  const [viewTime, setViewTime] = useState(() => nearestSlot(clockHHMM(new Date())));
  const isLiveNow = viewTime === nearestSlot(clockHHMM(now));

  // Is the business open RIGHT NOW (real clock within a today opening window)? Drives the
  // "release a table → offer to rebook it immediately" flow.
  const isBusinessOpenNow = useMemo(() => {
    const m = engine.toMinutes(clockHHMM(now));
    return todayWindows.some((w) => m >= w.start && m < w.end);
  }, [now, todayWindows]);

  // Drag / tap-to-seat: currently picked-up guest.
  const [pickedGuestId, setPickedGuestId] = useState(null);
  const [dragOverTableId, setDragOverTableId] = useState(null);

  // Quick-add inline form. Default the time to "now" — a board walk-in is being seated now.
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ time: nearestSlot(clockHHMM(new Date())), name: '', party: '2', phone: '' });

  // Edit inline form.
  const [editId, setEditId] = useState(null);

  // Multi-select mode — "בחירה מרובה": pick several waiting parties and mark them all
  // "התיישבו" (seated) in one action (e.g. when a few tables sit down together).
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Per-table history panel (opened by clicking a table when not seating / not multi-selecting).
  const [historyTable, setHistoryTable] = useState(null); // the table object, or null = closed
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Table action popover (open by tapping a table on the sketch when not seating a picked
  // guest): manage the table's reservation — arrived/seated/no-show/cancel, walk-in, history.
  const [actionTableId, setActionTableId] = useState(null);
  const [walkForm, setWalkForm] = useState({ name: '', party: '2' });

  // Side panel collapse (desktop) — persisted so the operator's preference sticks.
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try { return localStorage.getItem('lbPanelCollapsed') === '1'; } catch (e) { return false; }
  });
  const togglePanel = useCallback(() => {
    setPanelCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('lbPanelCollapsed', next ? '1' : '0'); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  // Mobile slide-over open state (bottom sheet over the sketch).
  const [sheetOpen, setSheetOpen] = useState(false);

  // Transient toast (e.g. "table taken").
  const [toast, setToast] = useState('');
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // The minutes we compute the floor for: live clock when viewing "now", else the scrubber slot.
  const scrubberMinutes = isLiveNow
    ? engine.toMinutes(clockHHMM(now))
    : engine.toMinutes(viewTime);

  const tablesInZone = useMemo(
    () => tables.filter((t) => zoneId === 0 || Number(t.area_id) === Number(zoneId)),
    [tables, zoneId]
  );

  // Layout bounds for the POSITIONED floor sketch. Each table carries canvas-space
  // x/y (roughly 0–1000px, matching the /live editor). We normalize to PERCENTAGES
  // of the bounds so tiles stay inside a relative container and scale responsively —
  // no JS measurement needed. Padding keeps tiles off the container edges, and a min
  // bound avoids divide-by-zero / over-zoom when tables share coordinates.
  // Pure helper: layout bounds for ANY list of tables (reused by the single-zone sketch
  // AND by each per-area block in the stacked view). Normalizes x/y to % of the bounds.
  const boundsFor = useCallback((tableList) => {
    const PAD = 90; // canvas-space breathing room on ALL sides (incl. the origin side)
    let minX = Infinity;
    let minY = Infinity;
    let maxX = 0;
    let maxY = 0;
    (tableList || []).forEach((t) => {
      const x = Number(t.x) || 0;
      const y = Number(t.y) || 0;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
    if (!Number.isFinite(minX)) { minX = 0; minY = 0; }
    return {
      minX, minY, PAD,
      width: Math.max((maxX - minX) + 2 * PAD, 480),
      height: Math.max((maxY - minY) + 2 * PAD, 360),
    };
  }, []);

  const floorBounds = useMemo(() => boundsFor(tablesInZone), [boundsFor, tablesInZone]);

  // Compute each table's status AT the viewing time via the seating engine — over ALL tables
  // (keyed by String(tableId)), so both the single-zone sketch and the stacked per-area blocks
  // read the same map. Returns tableId -> { status:'free'|'occupied'|'upcoming', res }.
  const floorState = useMemo(
    () => engine.floorStateAt(reservations, tables, scrubberMinutes, seatingDuration, upcomingLead),
    [reservations, tables, scrubberMinutes, seatingDuration, upcomingLead]
  );

  // Stacked "all areas" view: one block per real area (zones minus the id-0 "all" sentinel)
  // that has ≥1 table, in zones order, plus a trailing block for any table whose area doesn't
  // match a known area (or area_id 0) so no table ever disappears. Persisted toggle.
  const [stackMode, setStackMode] = useState(() => {
    try { return localStorage.getItem('lbStackMode') || 'stacked'; } catch (e) { return 'stacked'; }
  });
  const toggleStackMode = useCallback(() => {
    setStackMode((m) => {
      const next = m === 'stacked' ? 'single' : 'stacked';
      try { localStorage.setItem('lbStackMode', next); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  const areaBlocks = useMemo(() => {
    const realAreas = zones.filter((z) => Number(z.id) !== 0);
    const blocks = [];
    const used = new Set();
    realAreas.forEach((area) => {
      const at = tables.filter((t) => Number(t.area_id) === Number(area.id));
      if (at.length) {
        at.forEach((t) => used.add(t.id));
        blocks.push({ id: area.id, name: area.name, tables: at, bounds: boundsFor(at) });
      }
    });
    const orphans = tables.filter((t) => !used.has(t.id));
    if (orphans.length) blocks.push({ id: 'other', name: 'ללא אזור', tables: orphans, bounds: boundsFor(orphans) });
    return blocks;
  }, [zones, tables, boundsFor]);

  // Scope for recommendations / free-table picker / stats. In stacked mode the zone selector
  // is hidden and ALL areas are shown, so a stale zoneId must NOT constrain these — use all
  // tables and no area filter. In single mode, keep the selected zone's scope.
  const scopedTables = stackMode === 'stacked' ? tables : tablesInZone;
  const scopedAreaId = (stackMode === 'stacked' || zoneId === 0) ? null : zoneId;

  // Suggested next-free table for the currently picked guest (visual hint / recommendation).
  const suggestedTableId = useMemo(() => {
    if (!pickedGuestId) return null;
    const g = reservations.find((r) => r.id === pickedGuestId);
    if (!g) return null;
    const t = engine.suggestNextTable(reservations, scopedTables, {
      atMinutes: scrubberMinutes,
      party: g.party,
      areaId: scopedAreaId,
      durationMin: seatingDuration,
    });
    return t ? t.id : null;
  }, [pickedGuestId, reservations, scopedTables, scrubberMinutes, scopedAreaId, seatingDuration]);

  // EXPLAINED best-fit recommendation for the currently picked guest — the product's core
  // differentiator: PROPOSE the best table AND EXPLAIN WHY (assistive; the hostess confirms or
  // picks an alternative). Uses the SAME scopedTables/scopedAreaId as suggestedTableId, so the
  // sketch highlight and this card always agree. Null when no guest is picked / nothing fits.
  const recommendation = useMemo(() => {
    if (!pickedGuestId) return null;
    const g = reservations.find((r) => r.id === pickedGuestId);
    if (!g) return null;
    return engine.recommendTableExplained(reservations, scopedTables, {
      atMinutes: scrubberMinutes,
      party: g.party,
      areaId: scopedAreaId,
      durationMin: seatingDuration,
    });
  }, [pickedGuestId, reservations, scopedTables, scrubberMinutes, scopedAreaId, seatingDuration]);

  // Left guest list — today's reservations sorted by time.
  const guests = useMemo(
    () => [...reservations].sort((a, b) => engine.toMinutes(a.time) - engine.toMinutes(b.time)),
    [reservations]
  );

  // Quick-stats — recomputed from real data via the engine where possible.
  const stats = useMemo(() => {
    const total = reservations.length;
    const covers = reservations.reduce((sum, r) => sum + (Number(r.party) || 0), 0);

    // Current occupancy at the viewing time.
    const occNow = engine.occupancyAt(reservations, scopedTables, scrubberMinutes, seatingDuration);

    // Peak occupancy: scan every slot of the day and keep the max occupied-tables count.
    let peak = 0;
    TIME_SLOTS.forEach((s) => {
      const o = engine.occupancyAt(reservations, scopedTables, engine.toMinutes(s), seatingDuration);
      if (o.occupiedTables > peak) peak = o.occupiedTables;
    });

    // Waiting = reservations without a table assigned yet. reservationCreate stores an
    // unassigned booking as tableID 0 (not null), so treat 0/null both as "no table".
    const waiting = reservations.filter((r) => !(r.tableId != null && Number(r.tableId) > 0)).length;

    return { total, covers, occupied: occNow.occupiedTables, freeTables: occNow.freeTables, peak, waiting };
  }, [reservations, scopedTables, scrubberMinutes, seatingDuration]);

  /* ---- seating logic (shared by drag-drop and tap fallback) ---- */
  // Keep a ref of the async-seating in flight so double-drops don't double-fire.
  const seatingRef = useRef(false);
  // Same guard for the async walk-in create, so a double-submit can't create two rows.
  const addingRef = useRef(false);
  // Monotonic guard so an in-flight 45s poll can't clobber a just-applied local mutation
  // (seat/create/unseat/edit). Any local reservations change bumps this; a poll commits only
  // if its captured seq still matches — else the operator's fresh change would flicker away.
  const reservationsSeqRef = useRef(0);
  const bumpReservations = useCallback((updater) => {
    reservationsSeqRef.current += 1;
    setReservations(updater);
  }, []);
  // Guards the sequential bulk-seat loop against a double-fire.
  const bulkRef = useRef(false);
  // Monotonic guard so a slower table-history response can't land after a newer open/close.
  const historySeqRef = useRef(0);

  // Free tables (only) for the currently picked guest — powers the "בחר שולחן" list picker.
  const freeTablesForPicked = useMemo(() => {
    if (!pickedGuestId) return [];
    const g = reservations.find((r) => r.id === pickedGuestId);
    if (!g) return [];
    return engine.freeTablesFor(reservations, scopedTables, {
      atMinutes: scrubberMinutes,
      party: g.party,
      areaId: scopedAreaId,
      durationMin: seatingDuration,
    });
  }, [pickedGuestId, reservations, scopedTables, scrubberMinutes, scopedAreaId, seatingDuration]);

  // area id → name, so the table list can show which area each free table is in.
  const areaNameById = useMemo(() => {
    const m = new Map();
    zones.forEach((z) => m.set(Number(z.id), z.name));
    return m;
  }, [zones]);

  const seatGuestAtTable = useCallback(
    async (guestId, tableId) => {
      // Guard: only FREE tables (at the current scrubber time) may be seated onto.
      // Reuse the same occupied/upcoming toasts as before.
      const table = tables.find((t) => t.id === tableId);
      // Pass the table so its per-table hold time (holdTime) is honored; absent => business default.
      const free = engine.isTableFree(reservations, tableId, scrubberMinutes, seatingDuration, table);
      const tname = table?.name ?? tableId;
      if (!free) {
        const cell = floorState.get(String(tableId));
        const occ = cell?.res;
        setToast(
          cell?.status === 'upcoming'
            ? `שולחן ${tname} שמור להזמנה קרובה`
            : `שולחן ${tname} תפוס${occ ? ` (${occ.name})` : ''}`
        );
        return;
      }
      const guest = reservations.find((r) => r.id === guestId);
      if (!guest || !table) return;
      // A walk-in whose save failed has a LOCAL (non-numeric) id — it isn't a real server row,
      // so don't send ID=0 (the server would reject 316). Be honest instead.
      if (live && !/^\d+$/.test(String(guest.id))) {
        setToast(`${guest.name} נשמר מקומית בלבד — לא ניתן להושיב בשרת`);
        return;
      }
      if (seatingRef.current) return;
      seatingRef.current = true;

      // Persist to the server (falls back to offline/demo if the act isn't live).
      let result = { ok: false, offline: true };
      try {
        result = await reservationsSvc.assignTable({ reservationId: guest.id, tableId });
      } catch (e) { result = { ok: false, offline: true }; }
      seatingRef.current = false;

      // Only under REAL live data do we treat a non-OK, non-offline result as a true
      // server rejection (don't fake it). In demo/mock mode we always apply locally.
      if (live && !result.ok && !result.offline) {
        setToast(result.status === 409
          ? `שולחן ${tname} כבר תפוס`
          : `לא ניתן להושיב את ${guest.name} בשולחן ${tname} — נדחה ע"י השרת`);
        return;
      }
      // OK (persisted) or offline/demo (act not live yet) → update locally.
      bumpReservations((prev) =>
        prev.map((r) => (r.id === guestId ? { ...r, status: 'seated', tableId } : r))
      );
      setPickedGuestId(null);
      setToast(
        result.ok
          ? `${guest.name} ישוב בשולחן ${tname}`
          : `${guest.name} ישוב בשולחן ${tname} (הדגמה — לא נשמר בשרת)`
      );
    },
    [reservations, tables, floorState, scrubberMinutes, seatingDuration, live]
  );

  const closeHistory = useCallback(() => {
    historySeqRef.current += 1; // invalidate any in-flight history fetch
    setHistoryTable(null);
  }, []);

  // Open a table's reservation history (real, business-scoped — tableHistory act). A seq guard
  // ensures only the latest open commits its rows (fast A→B clicks can't cross-populate).
  const openTableHistory = useCallback(async (tableId) => {
    const seq = ++historySeqRef.current;
    const t = tables.find((x) => x.id === tableId) || { id: tableId, name: String(tableId) };
    setHistoryTable(t);
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const rows = await reservationsSvc.tableHistory(tableId);
      if (seq !== historySeqRef.current) return; // superseded by a newer open/close
      setHistoryRows(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (seq === historySeqRef.current) setHistoryRows([]);
    }
    if (seq === historySeqRef.current) setHistoryLoading(false);
  }, [tables]);

  const onTableClick = (tableId) => {
    // Seating a picked guest takes priority; otherwise (not multi-selecting) open the table's
    // action popover (manage arrival/status/walk-in; history is one action inside it).
    if (pickedGuestId) { seatGuestAtTable(pickedGuestId, tableId); return; }
    if (multiSelect) return;
    setWalkForm({ name: '', party: '2' });
    setActionTableId(tableId);
  };

  const onGuestClick = (guestId) => {
    const g = reservations.find((r) => r.id === guestId);
    if (!g || g.status === 'seated') return;
    setPickedGuestId((cur) => (cur === guestId ? null : guestId));
  };

  const unseatGuest = (guestId) => {
    bumpReservations((prev) =>
      prev.map((r) => (r.id === guestId ? { ...r, status: 'waiting', tableId: null } : r))
    );
    // These reservation-mutation acts aren't live yet; be honest under real data.
    if (live) setToast('שוחרר (מקומי — לא נשמר בשרת עדיין)');
  };

  // A party that can still be marked "התיישבו" (not already seated / cancelled / no-show).
  const canSeat = (g) => g && !['seated', 'cancelled', 'no-show'].includes(g.status);

  // Mark ONE party seated ("התיישבו") — persists via reservationSetStatus. Optimistic; the
  // 45s poll reconciles if the server rejects (a local-only walk-in can't persist).
  const markSeated = useCallback(async (guestId) => {
    const g = reservations.find((r) => r.id === guestId);
    if (!canSeat(g)) return;
    bumpReservations((prev) => prev.map((r) => (r.id === guestId ? { ...r, status: 'seated' } : r)));
    if (live && /^\d+$/.test(String(guestId))) {
      try {
        const res = await reservationsSvc.setStatus({ reservationId: guestId, status: 'seated' });
        if (!res.ok) setToast(`לא ניתן לעדכן את ${g.name} — נסו שוב`);
        else setToast(`${g.name} · התיישבו`);
      } catch (e) { setToast('שמירה נכשלה'); }
    } else if (live) {
      setToast('נשמר מקומית בלבד — הזמנה שלא נשמרה בשרת');
    }
  }, [reservations, live, bumpReservations]);

  // Set ANY lifecycle status on ONE reservation directly from the board/sketch popover
  // (arrived / seated / no-show / cancelled). Optimistic + live-persist for real (numeric-id)
  // rows; honest offline otherwise. no-show/cancelled also free the table locally.
  const STATUS_VERB = { arrived: 'סומן כהגיע', seated: 'התיישב', 'no-show': 'סומן כלא הגיע', cancelled: 'ההזמנה בוטלה' };
  const setGuestStatus = useCallback(async (guestId, status) => {
    const g = reservations.find((r) => r.id === guestId);
    if (!g) return;
    const frees = status === 'no-show' || status === 'cancelled';
    bumpReservations((prev) => prev.map((r) => (
      r.id === guestId ? { ...r, status, ...(frees ? { tableId: null } : {}) } : r
    )));
    const verb = STATUS_VERB[status] || 'עודכן';
    if (live && /^\d+$/.test(String(guestId))) {
      try {
        const res = await reservationsSvc.setStatus({ reservationId: guestId, status });
        if (!res.ok) setToast(`לא ניתן לעדכן את ${g.name} — נסו שוב`);
        else setToast(`${g.name} · ${verb}`);
      } catch (e) { setToast('שמירה נכשלה'); }
    } else if (live) {
      setToast('נשמר מקומית בלבד — הזמנה שלא נשמרה בשרת');
    }
  }, [reservations, live, bumpReservations]);

  // Walk-in (מזדמן) seated directly onto a specific FREE table from its popover: create the
  // reservation WITH the table (server conflict-checks), then set it seated (create defaults to
  // 'booked'), then optimistically insert it. addingRef guards a double-submit.
  const walkInAtTable = useCallback(async (tableId, name, party) => {
    if (addingRef.current) return;
    const table = tables.find((t) => t.id === tableId);
    if (!table) return;
    // Pass the table so its per-table hold time (holdTime) is honored; absent => business default.
    if (!engine.isTableFree(reservations, tableId, scrubberMinutes, seatingDuration, table)) {
      setToast(`שולחן ${table.name} כבר תפוס`); return;
    }
    const nm = (name || '').trim() || 'אורח מזדמן';
    const pty = Number(party) || 1;
    const d = new Date();
    const dateISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const nowHHMM = nearestSlot(clockHHMM(new Date()));
    addingRef.current = true;
    let res = { ok: false, offline: true };
    try {
      res = await reservationsSvc.create({
        date: dateISO,
        startTime: `${nowHHMM}:00`,
        numberOfGuests: pty,
        guestName: nm,
        area: Number(table.area_id) || 0,
        tableId,
      });
    } catch (e) { res = { ok: false, offline: true }; }

    // LIVE mode: never fake occupancy. Commit locally ONLY if the server truly accepted the
    // create (ok + a real numeric id). On rejection, be specific and don't touch the board.
    if (live && !res.offline) {
      const newId = res?.data?.ID ?? res?.data?.id ?? res?.data?.reservationID ?? null;
      if (!res.ok || newId == null || !/^\d+$/.test(String(newId))) {
        addingRef.current = false;
        setActionTableId(null);
        setToast(
          res.status === 409 ? `שולחן ${table.name} כבר תפוס`
            : res.status === 418 ? 'אין מקום פנוי מתאים לשולחן זה'
            : `לא ניתן להושיב מזדמן בשולחן ${table.name} — נדחה ע"י השרת`
        );
        return;
      }
      // Promote 'booked' → 'seated'; only claim "seated" if that write actually succeeded.
      let seatedOk = false;
      try { const s = await reservationsSvc.setStatus({ reservationId: newId, status: 'seated' }); seatedOk = !!s.ok; } catch (e) { /* leave booked */ }
      addingRef.current = false;
      bumpReservations((prev) => [...prev, {
        id: String(newId), date: dateISO, time: nowHHMM, name: nm, party: pty,
        phone: '', status: seatedOk ? 'seated' : 'booked', area: table.area_id, tableId,
      }]);
      setActionTableId(null);
      setToast(seatedOk
        ? `${nm} · הושב בשולחן ${table.name}`
        : `${nm} · נוסף לשולחן ${table.name} (עדכון הושבה נכשל — יעודכן)`);
      return;
    }

    // Demo/offline (act not live): optimistic local insert only.
    addingRef.current = false;
    bumpReservations((prev) => [...prev, {
      id: `walk-${Date.now()}`, date: dateISO, time: nowHHMM, name: nm, party: pty,
      phone: '', status: 'seated', area: table.area_id, tableId,
    }]);
    setActionTableId(null);
    setToast(`${nm} · הושב בשולחן ${table.name} (הדגמה — לא נשמר בשרת)`);
  }, [tables, reservations, scrubberMinutes, seatingDuration, live, bumpReservations]);

  const toggleSelected = (guestId) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(guestId)) next.delete(guestId); else next.add(guestId);
    return next;
  });
  const exitMultiSelect = () => { setMultiSelect(false); setSelectedIds(new Set()); };

  // Keep the selection consistent: if a poll (or another action) makes a selected party no
  // longer eligible (now seated/cancelled/no-show or gone), drop it from the selection.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(reservations.filter((r) => canSeat(r)).map((r) => r.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => { if (valid.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations]);

  // Mark ALL selected parties seated at once ("בחירה מרובה" → סמן שהתיישבו). Eligibility is
  // RE-CHECKED here against the current reservations, so a stale selection (after a poll or
  // another operator's change) can never flip a cancelled/no-show/absent reservation to seated.
  const bulkMarkSeated = async () => {
    if (bulkRef.current) return;
    const eligible = reservations.filter((r) => selectedIds.has(r.id) && canSeat(r));
    const ids = eligible.map((r) => r.id);
    if (!ids.length) { exitMultiSelect(); setToast('אין הזמנות מתאימות לסימון'); return; }
    bulkRef.current = true;
    const idSet = new Set(ids);
    bumpReservations((prev) => prev.map((r) => (idSet.has(r.id) ? { ...r, status: 'seated' } : r)));
    let failed = 0;
    try {
      if (live) {
        for (const id of ids) {
          if (!/^\d+$/.test(String(id))) { failed += 1; continue; }
          try {
            const res = await reservationsSvc.setStatus({ reservationId: id, status: 'seated' });
            if (!res.ok) failed += 1;
          } catch (e) { failed += 1; }
        }
      }
    } finally {
      bulkRef.current = false;
    }
    exitMultiSelect();
    setToast(failed
      ? `סומנו ${ids.length - failed}/${ids.length} כהתיישבו`
      : `סומנו ${ids.length} כהתיישבו`);
  };

  /* ---- quick add (walk-in) — persists via reservationCreate so it survives a refresh
     and can be seated on a real table. ---- */
  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || addingRef.current) return;
    const party = Number(form.party) || 1;
    const name = form.name.trim();
    const phone = form.phone.trim();
    const d = new Date();
    const dateISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    addingRef.current = true;
    let res = { ok: false, offline: true };
    try {
      res = await reservationsSvc.create({
        date: dateISO,
        startTime: `${form.time}:00`,
        numberOfGuests: party,
        guestName: name,
        guestPhone: phone,
        area: scopedAreaId || 0,
      });
    } catch (err) { res = { ok: false, offline: true }; }
    addingRef.current = false;

    // Use the REAL server id when saved (so seating can persist against it); fall back to a
    // local id only when the save didn't land.
    const realId = res.ok && res.data ? (res.data.ID ?? res.data.id) : null;
    const newId = realId != null ? String(realId) : `r${Date.now()}`;
    bumpReservations((prev) => [
      ...prev,
      { id: newId, time: form.time, name, party, phone, status: 'booked', tableId: null },
    ]);
    setForm({ time: nearestSlot(clockHHMM(new Date())), name: '', party: '2', phone: '' });
    setShowAdd(false);
    if (res.ok) setToast('ההזמנה נוספה ונשמרה');
    else setToast(live ? 'ההזמנה נוספה מקומית — לא נשמרה בשרת' : 'הזמנה נוספה');
  };

  /* ---- edit ---- */
  const submitEdit = (e, guestId) => {
    e.preventDefault();
    bumpReservations((prev) =>
      prev.map((r) =>
        r.id === guestId
          ? { ...r, time: form.time, name: form.name.trim() || r.name, party: Number(form.party) || r.party, phone: form.phone.trim() }
          : r
      )
    );
    setEditId(null);
    if (live) setToast('עודכן (מקומי — לא נשמר בשרת עדיין)');
  };

  const startEdit = (g) => {
    setForm({ time: g.time, name: g.name, party: String(g.party), phone: g.phone || '' });
    setEditId(g.id);
    setShowAdd(false);
  };

  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ *
   *  Side-panel content (stats + guest list) — shared by the desktop
   *  side panel AND the mobile bottom sheet. Extracted as a render var
   *  so ALL handlers stay identical; only the wrapper differs.
   * ------------------------------------------------------------------ */
  const panelContent = (
    <>
      {/* COMPACT stats — small rows instead of the old big cards */}
      <div className="lb-stat-rows" aria-label="נתונים מהירים">
        <div className="lb-stat-row">
          <span className="lb-stat-row-num lb-stat-brand">{stats.covers}</span>
          <span className="lb-stat-row-lbl">סועדים</span>
        </div>
        <div className="lb-stat-row">
          <span className="lb-stat-row-num lb-stat-ok">{stats.freeTables}</span>
          <span className="lb-stat-row-lbl">שולחנות פנויים</span>
        </div>
        <div className="lb-stat-row">
          <span className="lb-stat-row-num">{stats.waiting}</span>
          <span className="lb-stat-row-lbl">ממתינים</span>
        </div>
        <div className="lb-stat-row">
          <span className="lb-stat-row-num">{stats.total}</span>
          <span className="lb-stat-row-lbl">הזמנות היום</span>
        </div>
      </div>

      {/* EXPLAINED best-fit recommendation — appears when a guest is picked. The system
          PROPOSES a table and EXPLAINS WHY (deterministic rules, no black box); the hostess
          confirms ("הושב כאן") or picks an alternative. Assistive — nothing auto-happens. */}
      {recommendation && (
        <div className="lb-reco" role="region" aria-label="המלצת הושבה">
          <div className="lb-reco-head">
            <span className="lb-reco-title">המלצה חכמה</span>
            <span className="lb-reco-score">התאמה {recommendation.score}%</span>
          </div>
          <div className="lb-reco-table">{recommendation.table.name ?? `שולחן ${recommendation.table.id}`}</div>
          <ul className="lb-reco-reasons">
            {recommendation.reasons.map((r, i) => (
              <li key={i} className="lb-reco-reason">{r}</li>
            ))}
          </ul>
          <button
            type="button"
            className="lb-reco-primary"
            onClick={() => seatGuestAtTable(pickedGuestId, recommendation.table.id)}
          >
            הושב כאן
          </button>
          {recommendation.alternatives.length > 0 && (
            <div className="lb-reco-alts">
              <span className="lb-reco-alts-lbl">אפשרויות נוספות:</span>
              <div className="lb-reco-alts-row">
                {recommendation.alternatives.map((alt) => (
                  <button
                    key={alt.id}
                    type="button"
                    className="lb-reco-alt"
                    onClick={() => seatGuestAtTable(pickedGuestId, alt.id)}
                    title={`${alt.seats} מקומות`}
                  >
                    שולחן {alt.name} · {alt.seats}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="lb-guests-head">
        <h2 className="lb-guests-title">הזמנות היום</h2>
        <div className="lb-guests-head-actions">
          <button
            type="button"
            className={`lb-multi-btn ${multiSelect ? 'is-on' : ''}`}
            onClick={() => (multiSelect ? exitMultiSelect() : setMultiSelect(true))}
            title="סמן כמה שולחנות שהתיישבו יחד"
          >
            {multiSelect ? 'ביטול בחירה' : 'בחירה מרובה'}
          </button>
          <button
            type="button"
            className="lb-add-btn"
            onClick={() => { setShowAdd((v) => !v); setEditId(null); }}
          >
            ＋ הזמנה חדשה
          </button>
        </div>
      </div>

      {/* Bulk "התיישבו" bar — shows in multi-select once at least one party is selected. */}
      {multiSelect && selectedIds.size > 0 && (
        <div className="lb-bulkbar">
          <button type="button" className="lb-btn-primary" onClick={bulkMarkSeated}>
            סמן שהתיישבו ({selectedIds.size})
          </button>
          <button type="button" className="lb-btn-ghost" onClick={exitMultiSelect}>ביטול</button>
        </div>
      )}

      {showAdd && (
            <form className="lb-form" onSubmit={submitAdd}>
              <div className="lb-form-row">
                <input
                  className="lb-input lb-input-time"
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  aria-label="שעה"
                />
                <input
                  className="lb-input lb-input-party"
                  type="number"
                  min="1"
                  value={form.party}
                  onChange={(e) => setForm({ ...form, party: e.target.value })}
                  aria-label="מספר סועדים"
                />
              </div>
              <input
                className="lb-input"
                type="text"
                placeholder="שם האורח"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-label="שם"
                autoFocus
              />
              <input
                className="lb-input"
                type="tel"
                dir="ltr"
                placeholder="טלפון"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                aria-label="טלפון"
              />
              <div className="lb-form-actions">
                <button type="button" className="lb-btn-ghost" onClick={() => setShowAdd(false)}>ביטול</button>
                <button type="submit" className="lb-btn-primary">הוספה</button>
              </div>
            </form>
          )}

          <div className="lb-guest-list">
            {guests.length === 0 && (
              <div className="lb-empty">
                <div className="lb-empty-emoji" aria-hidden="true"><Icon name="utensils" size={30} /></div>
                <p>אין הזמנות להיום</p>
                <button type="button" className="lb-btn-primary" onClick={() => setShowAdd(true)}>
                  הוספת הזמנה
                </button>
              </div>
            )}

            {guests.map((g) => {
              const table = g.tableId ? tables.find((t) => t.id === g.tableId) : null;
              const picked = pickedGuestId === g.id;
              const draggable = g.status !== 'seated';

              if (editId === g.id) {
                return (
                  <form key={g.id} className="lb-form lb-form-inline" onSubmit={(e) => submitEdit(e, g.id)}>
                    <div className="lb-form-row">
                      <input className="lb-input lb-input-time" type="time" value={form.time}
                        onChange={(e) => setForm({ ...form, time: e.target.value })} aria-label="שעה" />
                      <input className="lb-input lb-input-party" type="number" min="1" value={form.party}
                        onChange={(e) => setForm({ ...form, party: e.target.value })} aria-label="סועדים" />
                    </div>
                    <input className="lb-input" type="text" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="שם" />
                    <input className="lb-input" type="tel" dir="ltr" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} aria-label="טלפון" />
                    <div className="lb-form-actions">
                      <button type="button" className="lb-btn-ghost" onClick={() => setEditId(null)}>ביטול</button>
                      <button type="submit" className="lb-btn-primary">שמירה</button>
                    </div>
                  </form>
                );
              }

              return (
                <React.Fragment key={g.id}>
                <article
                  className={`lb-guest ${picked ? 'is-picked' : ''} ${multiSelect && selectedIds.has(g.id) ? 'is-selected' : ''} status-${g.status}`}
                  draggable={!multiSelect && draggable}
                  onDragStart={(e) => {
                    setPickedGuestId(g.id);
                    e.dataTransfer.setData('text/plain', g.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => setDragOverTableId(null)}
                  onClick={() => (multiSelect ? (canSeat(g) && toggleSelected(g.id)) : onGuestClick(g.id))}
                >
                  {multiSelect && canSeat(g) && (
                    <input type="checkbox" className="lb-guest-check" checked={selectedIds.has(g.id)}
                      readOnly aria-label={`בחירת ${g.name}`} />
                  )}
                  <div className="lb-guest-time" dir="ltr">{g.time}</div>
                  <div className="lb-guest-body">
                    <div className="lb-guest-top">
                      <span className="lb-guest-name">{g.name}</span>
                      <span className={`lb-badge badge-${g.status}`}>{STATUS_LABEL[g.status]}</span>
                    </div>
                    <div className="lb-guest-meta">
                      <span className="lb-guest-party"><Icon name="users" size={14} style={{ verticalAlign: 'middle' }} /> {g.party}</span>
                      {g.phone && <span className="lb-guest-phone" dir="ltr">{g.phone}</span>}
                      {table && <span className="lb-guest-table">שולחן {table.name}</span>}
                    </div>
                  </div>
                  {!multiSelect && (
                  <div className="lb-guest-actions">
                    {canSeat(g) && (
                      <button
                        type="button"
                        className="lb-seat-btn"
                        title="סמן שהתיישבו"
                        onClick={(e) => { e.stopPropagation(); markSeated(g.id); }}
                      >התיישבו</button>
                    )}
                    <button
                      type="button"
                      className="lb-icon-btn"
                      title="עריכה"
                      onClick={(e) => { e.stopPropagation(); startEdit(g); }}
                    ><Icon name="edit" size={16} /></button>
                    {g.status === 'seated' ? (
                      <button
                        type="button"
                        className="lb-icon-btn"
                        title="שחרור שולחן"
                        onClick={(e) => { e.stopPropagation(); unseatGuest(g.id); }}
                      ><Icon name="chevron-left" size={16} /></button>
                    ) : (
                      <button
                        type="button"
                        className={`lb-icon-btn ${picked ? 'is-picked' : ''}`}
                        title="בחר שולחן להושבה"
                        aria-expanded={picked}
                        onClick={(e) => { e.stopPropagation(); onGuestClick(g.id); }}
                      ><Icon name="tables" size={16} /></button>
                    )}
                  </div>
                  )}
                </article>

                {/* Free-table LIST picker — appears under the picked (unseated) guest. Only
                    FREE tables are listed; picking one seats the guest (persists via the act). */}
                {picked && draggable && (
                  <div className="lb-tablepick" onClick={(e) => e.stopPropagation()}>
                    <div className="lb-tablepick-head">
                      בחר שולחן פנוי{g.party ? ` · ${g.party} סועדים` : ''}
                    </div>
                    {live && !/^\d+$/.test(String(g.id)) ? (
                      <div className="lb-tablepick-empty">נשמר מקומית בלבד — לא ניתן להושיב בשרת</div>
                    ) : freeTablesForPicked.length === 0 ? (
                      <div className="lb-tablepick-empty">אין שולחן פנוי בשעה זו</div>
                    ) : (
                      <ul className="lb-tablepick-list">
                        {freeTablesForPicked.map((t) => (
                          <li key={t.id}>
                            <button
                              type="button"
                              className={`lb-tablepick-item${t.id === suggestedTableId ? ' is-recommended' : ''}${t.fits ? '' : ' is-tight'}`}
                              onClick={(e) => { e.stopPropagation(); seatGuestAtTable(g.id, t.id); }}
                            >
                              <span className="lb-tablepick-name">שולחן {t.name}</span>
                              <span className="lb-tablepick-seats"><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {t.seats}</span>
                              {areaNameById.get(t.areaId) && <span className="lb-tablepick-area">{areaNameById.get(t.areaId)}</span>}
                              {t.id === suggestedTableId && <span className="lb-tablepick-rec">מומלץ</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>

      {pickedGuestId && (
        suggestedTableId
          ? <div className="lb-hint">בחרו שולחן מהרשימה, או לחצו על שולחן פנוי בלוח · המומלץ מסומן</div>
          : <div className="lb-hint">אין שולחן פנוי מתאים בשעה זו</div>
      )}
    </>
  );

  /* ------------------------------------------------------------------ */

  // Render ONE positioned table tile inside a sketch of the given bounds. Shared by the
  // single-zone sketch and every per-area block in the stacked view (same status colors,
  // drag-drop seat, click behaviour). `bounds` = boundsFor(the tables in that sketch).
  const renderTableTile = (t, bounds) => {
    const info = floorState.get(String(t.id)) || { status: 'free', res: null };
    const isDrop = dragOverTableId === t.id;
    const canSeatHere = info.status === 'free';
    const isSuggested = suggestedTableId === t.id;
    const seats = engine.tableSeats(t);
    const leftPct = (((Number(t.x) || 0) - bounds.minX + bounds.PAD) / bounds.width) * 100;
    const topPct = (((Number(t.y) || 0) - bounds.minY + bounds.PAD) / bounds.height) * 100;
    const shapeClass = t.shape === 'circle'
      ? 'shape-circle'
      : t.shape === 'rectangle'
        ? 'shape-rect'
        : 'shape-square';
    return (
      <button
        type="button"
        key={t.id}
        className={`lb-table ${shapeClass} status-${info.status} ${isDrop ? 'is-dropover' : ''} ${pickedGuestId && canSeatHere ? 'is-target' : ''} ${isSuggested ? 'is-recommended' : ''}`}
        style={{ left: `${leftPct}%`, top: `${topPct}%` }}
        onClick={() => onTableClick(t.id)}
        onDragOver={(e) => { e.preventDefault(); setDragOverTableId(t.id); }}
        onDragLeave={() => setDragOverTableId((cur) => (cur === t.id ? null : cur))}
        onDrop={(e) => {
          e.preventDefault();
          const gid = e.dataTransfer.getData('text/plain') || pickedGuestId;
          setDragOverTableId(null);
          if (gid) seatGuestAtTable(gid, t.id);
        }}
        title={info.res ? `${info.res.name} · ${info.res.time}` : `שולחן ${t.name}`}
      >
        {isSuggested && <span className="lb-table-rec">מומלץ</span>}
        <span className="lb-table-name">{t.name}</span>
        <span className="lb-table-seats">{seats} מקומות</span>
        {info.res && (
          <span className="lb-table-guest">{info.res.name}</span>
        )}
        {info.status === 'upcoming' && info.res && (
          <span className="lb-table-upcoming">
            {info.res.time} · בעוד {Math.max(0, engine.toMinutes(info.res.time) - scrubberMinutes)}׳
          </span>
        )}
      </button>
    );
  };

  // Occupied-count for a per-area block header (occupied+upcoming vs total).
  const blockOccupancy = (blockTables) => {
    let occ = 0;
    blockTables.forEach((t) => {
      const s = (floorState.get(String(t.id)) || {}).status;
      if (s === 'occupied' || s === 'upcoming') occ += 1;
    });
    return { occ, total: blockTables.length };
  };

  return (
    <div className={`lb-page ${panelCollapsed ? 'panel-collapsed' : ''}`}>
      {/* ---------------- Slim top bar ---------------- */}
      <header className="lb-topbar">
        <div className="lb-topbar-lead">
          <h1 className="lb-title">לוח LIVE</h1>
          <span className="lb-clock" title="השעה עכשיו">
            <span className="lb-clock-dot" aria-hidden="true" />
            עכשיו {clockHHMM(now)}
          </span>
          <span className="lb-date">{todayHe()}</span>
          {stackMode === 'single' && (
            <label className="lb-zone">
              <span className="lb-zone-label">אזור</span>
              <select
                className="lb-zone-select"
                value={zoneId}
                onChange={(e) => setZoneId(Number(e.target.value))}
              >
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </label>
          )}
          {/* View toggle: all-areas stacked (area under area) vs a single zone. */}
          <div className="lb-viewmode" role="group" aria-label="מצב תצוגה">
            <button
              type="button"
              className={`lb-viewmode-btn ${stackMode === 'stacked' ? 'is-active' : ''}`}
              onClick={() => stackMode !== 'stacked' && toggleStackMode()}
            >
              <Icon name="layers" size={15} /> כל האזורים
            </button>
            <button
              type="button"
              className={`lb-viewmode-btn ${stackMode === 'single' ? 'is-active' : ''}`}
              onClick={() => stackMode !== 'single' && toggleStackMode()}
            >
              <Icon name="board" size={15} /> אזור בודד
            </button>
          </div>
        </div>

        <div className="lb-topbar-scrub">
          <span className="lb-scrubber-title">
            תצוגה לשעה <strong dir="ltr">{viewTime}</strong>
            {isLiveNow && <span className="lb-live-tag">עכשיו</span>}
          </span>
          {!isLiveNow && (
            <button
              type="button"
              className="lb-back-now"
              onClick={() => setViewTime(nearestSlot(clockHHMM(now)))}
            >
              חזרה לעכשיו
            </button>
          )}
          <div className="lb-slots" role="tablist" aria-label="בחירת שעת תצוגה">
            {TIME_SLOTS.map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={s === viewTime}
                dir="ltr"
                className={`lb-slot ${s === viewTime ? 'is-active' : ''}`}
                onClick={() => setViewTime(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* thin single-line live/offline note (no mock data anymore) */}
      {live ? (
        <div className="lb-demo-note lb-live-note"><Icon name="info" size={14} style={{ verticalAlign: 'middle' }} /> נתונים חיים · מחובר להזמנות האמיתיות</div>
      ) : (
        <div className="lb-demo-note"><Icon name="info" size={14} style={{ verticalAlign: 'middle' }} /> לא ניתן לטעון נתונים חיים כרגע</div>
      )}

      {/* ---------------- Main: dominant sketch + collapsible side panel ---------------- */}
      <div className="lb-main">
        {/* FLOOR — the dominant pane */}
        <section className="lb-floor" aria-label="מפת השולחנות">
          <div className="lb-legend">
            <span className="lb-legend-item"><i className="dot dot-free" />פנוי</span>
            <span className="lb-legend-item"><i className="dot dot-upcoming" />הזמנה קרובה</span>
            <span className="lb-legend-item"><i className="dot dot-occupied" />תפוס</span>
          </div>

          {tables.length === 0 ? (
            /* No sketch defined at all — guide the operator to build the table map. */
            <div className="lb-empty">
              <div className="lb-empty-emoji" aria-hidden="true"><Icon name="tables" size={30} /></div>
              <p>לא הוגדרה סקיצה — הקימו את מפת השולחנות במסך ‘שולחנות’.</p>
              <a className="lb-btn-primary lb-empty-link" href="/live">למסך השולחנות</a>
            </div>
          ) : stackMode === 'stacked' ? (
            /* STACKED: every area as its own sketch block, area under area. */
            <div className="lb-stack">
              {areaBlocks.map((block) => {
                const occ = blockOccupancy(block.tables);
                return (
                  <div className="lb-area-block" key={block.id}>
                    <div className="lb-area-head">
                      <span className="lb-area-name">{block.name}</span>
                      <span className="lb-area-occ">{occ.occ}/{occ.total} תפוסים</span>
                    </div>
                    <div
                      className="lb-sketch"
                      style={{ aspectRatio: `${block.bounds.width} / ${block.bounds.height}` }}
                    >
                      {block.tables.map((t) => renderTableTile(t, block.bounds))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : tablesInZone.length === 0 ? (
            <div className="lb-empty">
              <p>אין שולחנות באזור זה</p>
            </div>
          ) : (
            /* POSITIONED floor sketch: tiles placed at their real x/y (as % of the
               layout bounds) inside a relative, aspect-locked container. */
            <div
              className="lb-sketch"
              style={{ aspectRatio: `${floorBounds.width} / ${floorBounds.height}` }}
            >
              {tablesInZone.map((t) => renderTableTile(t, floorBounds))}
            </div>
          )}
        </section>

        {/* SIDE PANEL — collapsible; holds compact stats + guest list */}
        <aside
          className="lb-panel"
          aria-label="רשימת ההזמנות והנתונים"
          aria-hidden={panelCollapsed}
        >
          {/* When collapsed, render NOTHING inside so no control stays in the tab order
              (visual-hide alone leaves buttons/inputs focusable). The floating expand
              button below reopens it. */}
          {!panelCollapsed && (
            <>
              <div className="lb-panel-bar">
                <button
                  type="button"
                  className="lb-panel-toggle"
                  onClick={togglePanel}
                  aria-label="מזעור הפאנל"
                  title="מזעור הפאנל"
                >
                  <Icon name="chevron-right" size={18} />
                </button>
              </div>
              <div className="lb-panel-body">
                {panelContent}
              </div>
            </>
          )}
        </aside>

        {/* COLLAPSED — floating expand button pinned to the panel edge (desktop) */}
        {panelCollapsed && (
          <button
            type="button"
            className="lb-panel-expand"
            onClick={togglePanel}
            aria-label="פתיחת רשימת ההזמנות"
            title="פתיחת רשימת ההזמנות"
          >
            <Icon name="users" size={20} />
            <Icon name="chevron-left" size={16} />
          </button>
        )}
      </div>

      {/* MOBILE — floating button opens the guest list + stats as a bottom sheet */}
      <button
        type="button"
        className="lb-sheet-fab"
        onClick={() => setSheetOpen(true)}
        aria-label="פתיחת רשימת ההזמנות"
        title="רשימת ההזמנות"
      >
        <Icon name="users" size={22} />
      </button>

      {sheetOpen && (
        <div className="lb-sheet-scrim" onClick={() => setSheetOpen(false)}>
          <div
            className="lb-sheet"
            role="dialog"
            aria-label="רשימת ההזמנות והנתונים"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lb-sheet-bar">
              <span className="lb-sheet-grip" aria-hidden="true" />
              <button
                type="button"
                className="lb-panel-toggle"
                onClick={() => setSheetOpen(false)}
                aria-label="סגירה"
                title="סגירה"
              >
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="lb-panel-body">
              {panelContent}
            </div>
          </div>
        </div>
      )}

      {toast && <div className="lb-toast" role="status">{toast}</div>}

      {/* Table action popover — manage the tapped table's reservation right on the sketch:
          mark arrived / seated / no-show / cancel, or seat a walk-in on a free table. Reads
          the CURRENT table + cell + res from floorState each render (never a stale snapshot). */}
      {actionTableId != null && (() => {
        const t = tables.find((x) => x.id === actionTableId);
        if (!t) return null;
        const cell = floorState.get(String(t.id)) || { status: 'free', res: null };
        const res = cell.res;
        const seated = res && res.status === 'seated';
        const close = () => setActionTableId(null);
        const act = (fn) => { fn(); close(); };
        return (
          <div className="lb-hist-overlay" onClick={close}>
            <div className="lb-tact" role="dialog" aria-label={`ניהול שולחן ${t.name}`} onClick={(e) => e.stopPropagation()}>
              <div className="lb-hist-head">
                <h3 className="lb-hist-title">{t.name}</h3>
                <button type="button" className="lb-hist-close" onClick={close} aria-label="סגור">✕</button>
              </div>
              <div className="lb-tact-body">
                <div className={`lb-tact-status status-${cell.status}`}>
                  {cell.status === 'occupied' ? 'תפוס' : cell.status === 'upcoming' ? 'הזמנה קרובה' : 'פנוי'}
                  {res && <> · {res.name} · <span dir="ltr">{String(res.time || '').slice(0, 5)}</span> · <Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {res.party}</>}
                </div>

                {res ? (
                  <div className="lb-tact-actions">
                    {!seated && (
                      <>
                        {res.status !== 'arrived' && (
                          <button type="button" className="lb-tact-btn" onClick={() => act(() => setGuestStatus(res.id, 'arrived'))}>
                            <Icon name="check" size={16} /> סמן הגיע
                          </button>
                        )}
                        <button type="button" className="lb-tact-btn is-primary" onClick={() => act(() => setGuestStatus(res.id, 'seated'))}>
                          <Icon name="check" size={16} /> הושבה
                        </button>
                      </>
                    )}
                    {seated && (
                      <button type="button" className="lb-tact-btn"
                        onClick={() => {
                          // Release the table. If the business is STILL OPEN, keep the popover
                          // open — the table becomes free, so it re-renders as the walk-in /
                          // rebook form and the hostess can seat the next guest immediately.
                          unseatGuest(res.id);
                          if (isBusinessOpenNow) {
                            setWalkForm({ name: '', party: String(res.party || 2) });
                            setToast(`שולחן ${t.name} שוחרר · ניתן להזמין מיד`);
                          } else {
                            close();
                          }
                        }}>
                        <Icon name="board" size={16} /> שחרור שולחן
                      </button>
                    )}
                    <button type="button" className="lb-tact-btn is-warn" onClick={() => act(() => setGuestStatus(res.id, 'no-show'))}>
                      <Icon name="ban" size={16} /> לא הגיע
                    </button>
                    <button type="button" className="lb-tact-btn is-danger" onClick={() => act(() => setGuestStatus(res.id, 'cancelled'))}>
                      <Icon name="x" size={16} /> ביטול הזמנה
                    </button>
                  </div>
                ) : (
                  <form
                    className="lb-tact-walk"
                    onSubmit={(e) => { e.preventDefault(); walkInAtTable(t.id, walkForm.name, walkForm.party); }}
                  >
                    <div className="lb-tact-walk-title">הזמנה מזדמנת כאן</div>
                    <input
                      className="lb-tact-input" type="text" placeholder="שם האורח"
                      value={walkForm.name} onChange={(e) => setWalkForm((f) => ({ ...f, name: e.target.value }))} autoFocus
                    />
                    <label className="lb-tact-party">
                      סועדים:
                      <input
                        className="lb-tact-input lb-tact-party-input" type="number" min="1" max="99"
                        value={walkForm.party} onChange={(e) => setWalkForm((f) => ({ ...f, party: e.target.value }))}
                      />
                    </label>
                    <button type="submit" className="lb-tact-btn is-primary">
                      <Icon name="plus" size={16} /> הושב עכשיו
                    </button>
                  </form>
                )}

                <button type="button" className="lb-tact-btn is-ghost" onClick={() => { close(); openTableHistory(t.id); }}>
                  <Icon name="list" size={16} /> היסטוריית שולחן
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Per-table history — real reservations for the clicked table (tableHistory act). */}
      {historyTable && (
        <div className="lb-hist-overlay" onClick={closeHistory}>
          <div className="lb-hist" role="dialog" aria-label="היסטוריית שולחן" onClick={(e) => e.stopPropagation()}>
            <div className="lb-hist-head">
              <h3 className="lb-hist-title">היסטוריית שולחן {historyTable.name}</h3>
              <button type="button" className="lb-hist-close" onClick={closeHistory} aria-label="סגור">✕</button>
            </div>
            <div className="lb-hist-body">
              {historyLoading ? (
                <div className="lb-hist-empty">טוען…</div>
              ) : historyRows.length === 0 ? (
                <div className="lb-hist-empty">אין היסטוריה לשולחן זה</div>
              ) : (
                <ul className="lb-hist-list">
                  {historyRows.map((r, i) => (
                    <li key={i} className="lb-hist-row">
                      <span className="lb-hist-date" dir="ltr">{String(r.date || '').slice(0, 10)}</span>
                      <span className="lb-hist-time" dir="ltr">{String(r.time || '').slice(0, 5)}</span>
                      <span className="lb-hist-name">{r.name || '—'}</span>
                      <span className="lb-hist-party"><Icon name="users" size={13} style={{ verticalAlign: 'middle' }} /> {r.party}</span>
                      <span className={`lb-badge badge-${r.status}`}>{STATUS_LABEL[r.status] || r.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveBoard;
